import { useEffect, useId, useRef, useState } from "react";
import {
    GetMailboxConfiguration,
    MailboxEffectiveConfiguration,
    ResolvedDirection,
    ResolvedMailConfiguration,
} from "./MailboxTestAPI";

interface iResolvedSettingsDialogProps {
    token: string;
    mailboxId: number;
    mailboxName: string;
    unsavedChanges: boolean;
    onClose: () => void;
}

type LoadState =
    | { kind: "loading" }
    | { kind: "ready"; configuration: MailboxEffectiveConfiguration }
    | { kind: "forbidden" }
    | { kind: "notFound" }
    | { kind: "unauthorised" }
    | { kind: "failed" };

/**
 * The fields always listed, in reading order. Absent ones show "not set",
 * because a missing server or username IS the finding when a test fails at
 * `Resolve`.
 */
const STANDARD_FIELDS: { key: keyof ResolvedMailConfiguration; label: string }[] = [
    { key: "provider", label: "Provider" },
    { key: "identity", label: "Identity" },
    { key: "server", label: "Server" },
    { key: "port", label: "Port" },
    { key: "username", label: "Username" },
    { key: "password", label: "Password" },
];

/** Listed only when present. Basic mailboxes have none, and "not set" ×3 would read as a fault. */
const MSAL_FIELDS: { key: keyof ResolvedMailConfiguration; label: string }[] = [
    { key: "msalTenantId", label: "MSAL tenant ID" },
    { key: "msalClientId", label: "MSAL client ID" },
    { key: "msalClientSecret", label: "MSAL client secret" },
];

function Direction({ title, cloud, direction }: { title: string; cloud: boolean; direction: ResolvedDirection }) {
    const settings = direction.settings;
    return (
        <section className="mbt-direction">
            <div className="mbt-direction-head">
                <h3>{title}</h3>
                <span className="mbt-detail">Cloud processing: {cloud ? "on" : "off"}</span>
            </div>

            {direction.error !== undefined ? (
                <p className="mbt-result mbt-fail">{direction.error}</p>
            ) : !settings ? (
                <p className="mbt-detail">No settings were returned for this direction.</p>
            ) : (
                <>
                    {!settings.connectorRegistered && (
                        <p className="mbt-result mbt-warn">
                            <b>No connector is registered for {settings.provider ?? "this provider"}.</b> The processor
                            cannot use these settings.
                        </p>
                    )}
                    <dl className="mbt-fields">
                        {STANDARD_FIELDS.map(({ key, label }) => (
                            <Field
                                key={key}
                                label={label}
                                value={settings[key]}
                            />
                        ))}
                        {MSAL_FIELDS.filter(({ key }) => settings[key] !== undefined).map(({ key, label }) => (
                            <Field
                                key={key}
                                label={label}
                                value={settings[key]}
                            />
                        ))}
                    </dl>
                </>
            )}
        </section>
    );
}

/**
 * One read-only field. Secrets arrive masked by the server and are rendered
 * exactly as received — there is no reveal, and nothing here could unmask
 * them if there were.
 */
function Field({ label, value }: { label: string; value: string | number | boolean | undefined }) {
    return (
        <>
            <dt>{label}</dt>
            <dd>{value === undefined || value === "" ? <span className="mbt-unset">not set</span> : String(value)}</dd>
        </>
    );
}

/**
 * The effective connection settings the mail processor resolves for this
 * mailbox, read from `GET .../Configuration`.
 *
 * The first modal in this app. `ActionBar` deliberately avoided one for an
 * inline confirm; this is different in kind — a read-only reference the admin
 * glances at and dismisses, two columns wide, that would push the whole edit
 * form down if drawn inline. Escape, the close button and a backdrop click
 * all dismiss it, and focus moves to the close button on open.
 */
function ResolvedSettingsDialog({
    token,
    mailboxId,
    mailboxName,
    unsavedChanges,
    onClose,
}: iResolvedSettingsDialogProps) {
    const [state, setState] = useState<LoadState>({ kind: "loading" });
    const titleId = useId();
    const closeRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const response = await GetMailboxConfiguration(token, mailboxId);
            if (cancelled) {
                return;
            }
            if (response.status === 200 && response.data) {
                setState({ kind: "ready", configuration: response.data });
            } else if (response.status === 403) {
                setState({ kind: "forbidden" });
            } else if (response.status === 404) {
                setState({ kind: "notFound" });
            } else if (response.status === 401) {
                setState({ kind: "unauthorised" });
            } else {
                setState({ kind: "failed" });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token, mailboxId]);

    // Through a ref so a parent passing a fresh arrow each render does not
    // re-run the effect below, which would steal focus back to Close.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        closeRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onCloseRef.current();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    return (
        <div
            className="mbt-backdrop"
            onMouseDown={event => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                className="mbt-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
            >
                <div className="mbt-dialog-head">
                    <h2 id={titleId}>Resolved settings — {mailboxName}</h2>
                    <button
                        ref={closeRef}
                        type="button"
                        className="g-btn btn-default"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>

                <p className="mbt-detail">
                    What the mail processor uses for this mailbox, from the saved settings. Secrets are masked by the
                    server.
                </p>
                {unsavedChanges && (
                    <p className="mbt-note">Your unsaved changes are not included until you update the mailbox.</p>
                )}

                {state.kind === "loading" && <p className="mbt-detail">Loading…</p>}
                {state.kind === "forbidden" && (
                    <p className="mbt-result mbt-fail">
                        <b>Not authorised.</b> Resolved settings need a sys-admin user.
                    </p>
                )}
                {state.kind === "notFound" && (
                    <p className="mbt-result mbt-fail">
                        <b>Mailbox not found.</b> It may have been removed. Reload the list.
                    </p>
                )}
                {state.kind === "unauthorised" && (
                    <p className="mbt-result mbt-fail">Your session has expired. Reload the page to sign in again.</p>
                )}
                {state.kind === "failed" && (
                    <p className="mbt-result mbt-fail">
                        <b>The settings could not be loaded.</b> Close this and try again.
                    </p>
                )}
                {state.kind === "ready" && (
                    <div className="mbt-directions">
                        <Direction
                            title="Inbound"
                            cloud={state.configuration.cloudProcessing.inbound}
                            direction={state.configuration.inbound}
                        />
                        <Direction
                            title="Outbound"
                            cloud={state.configuration.cloudProcessing.outbound}
                            direction={state.configuration.outbound}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default ResolvedSettingsDialog;
